// The outer promenade, the haze over the sea, pigeons and the flower stall.
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { PI } from '../lib/math.js';
import { pick, rnd, rr } from '../lib/random.js';
import { scene } from '../core/scene.js';
import { M, P, add, canvasTex } from '../lib/factory.js';
import { instanced } from '../lib/instancing.js';
import { grassMat } from '../materials/surfaces.js';
import { ISLAND } from './island.js';
import { colliders } from './eixample.js';
import { mergeAny, tint } from './facade-details.js';
import { lampHeadMat } from './street-furniture.js';
import { BENCH, benchGeo, makePalm } from './park.js';
import { makeDog, makePerson, walkers } from '../people/models.js';
import { sitSpots } from '../people/crowd.js';

export let pigeons;

export function buildPromenade() {
  pigeons = [];
  const puffTexLater = () =>
    canvasTex(64, 64, (c) => {
      const g = c.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, 'rgba(255,255,255,.95)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      c.fillStyle = g;
      c.fillRect(0, 0, 64, 64);
    });
  // ---------- the outer promenade: furniture, planting, volume, and a soft edge to the world ----------
  // points along the island's rounded outline, inset from the edge (skips the beach side)
  function rimPoints(inset, step) {
    const E = ISLAND - inset,
      R = Math.max(1, 14 - inset),
      out = [],
      pts = [];
    const seg = (ax, az, bx, bz) => {
      const L = Math.hypot(bx - ax, bz - az);
      for (let t = 0; t < L; t += step)
        pts.push([ax + ((bx - ax) * t) / L, az + ((bz - az) * t) / L]);
    };
    const arc = (cx, cz, a0) => {
      for (let t = 0; t < (R * PI) / 2; t += step) {
        const a = a0 + t / R;
        pts.push([cx + Math.cos(a) * R, cz + Math.sin(a) * R]);
      }
    };
    seg(-E, 50, -E, -E + R);
    arc(-E + R, -E + R, PI);
    seg(-E + R, -E, E - R, -E);
    arc(E - R, -E + R, -PI / 2);
    seg(E, -E + R, E, 50);
    for (let i = 0; i < pts.length; i++) {
      const [x, z] = pts[i],
        [x2, z2] = pts[Math.min(pts.length - 1, i + 1)],
        [x0, z0] = pts[Math.max(0, i - 1)];
      let tx = x2 - x0,
        tz = z2 - z0;
      const tl = Math.hypot(tx, tz) || 1;
      tx /= tl;
      tz /= tl;
      out.push({ x, z, nx: tz, nz: -tx, ry: Math.atan2(-tz, tx) });
    }
    return out;
  }
  {
    // sea wall with a stone balustrade at the island edge
    const rail = rimPoints(0.4, 1.05);
    instanced(
      new THREE.BoxGeometry(1.15, 0.9, 0.5),
      M(0xe2d3b5, { flatShading: false }),
      rail,
      (o, p) => {
        o.position.set(p.x, 0.75, p.z);
        o.rotation.y = p.ry;
      },
    );
    instanced(
      new THREE.BoxGeometry(1.2, 0.14, 0.62),
      M(0xf0e4cc, { flatShading: false }),
      rail,
      (o, p) => {
        o.position.set(p.x, 1.25, p.z);
        o.rotation.y = p.ry;
      },
    );
    instanced(
      new THREE.BoxGeometry(1.15, 5.6, 0.7),
      M(0xb9a786, { flatShading: false }),
      rail,
      (o, p) => {
        o.position.set(p.x + p.nx * 0.1, -2.6, p.z + p.nz * 0.1);
        o.rotation.y = p.ry;
      },
    );
    for (const p of rail) {
      colliders.push({ type: 'ell', cx: p.x, cz: p.z, a: 0.7, b: 0.7 });
    }
    // rock armour in the water, breaking up the straight edge
    const rocks = [];
    for (const p of rimPoints(-1.6, 0.9))
      for (let k = 0; k < 2; k++)
        rocks.push({
          x: p.x + p.nx * rr(-0.4, 2.6) + rr(-0.4, 0.4),
          z: p.z + p.nz * rr(-0.4, 2.6) + rr(-0.4, 0.4),
          s: rr(0.6, 1.5),
          r: [rr(0, PI), rr(0, PI), rr(0, PI)],
        });
    const im = new THREE.InstancedMesh(
        new THREE.DodecahedronGeometry(0.7, 0),
        M(0xffffff, { roughness: 0.95 }),
        rocks.length,
      ),
      o = new THREE.Object3D(),
      c = new THREE.Color();
    rocks.forEach((r, i) => {
      o.position.set(r.x, -0.55 + rr(0, 0.4), r.z);
      o.rotation.set(...r.r);
      o.scale.set(r.s, r.s * 0.7, r.s);
      o.updateMatrix();
      im.setMatrixAt(i, o.matrix);
      im.setColorAt(i, c.set(pick([0x9a948a, 0x857f76, 0xaaa398, 0x766f67])));
    });
    im.castShadow = im.receiveShadow = true;
    scene.add(im);
    // foam where the water meets the rocks
    const foamRing = [];
    for (const p of rimPoints(-3.6, 1.6)) foamRing.push(p);
    window.rimFoam = instanced(
      new THREE.PlaneGeometry(2.2, 1.3).rotateX(-PI / 2),
      new THREE.MeshBasicMaterial({
        map: puffTexLater(),
        transparent: true,
        depthWrite: false,
        opacity: 0.7,
      }),
      foamRing,
      (o, p) => {
        o.position.set(p.x, -0.3, p.z);
        o.rotation.y = p.ry;
      },
    );
    window.rimFoam.castShadow = window.rimFoam.receiveShadow = false;

    // promenade furniture along the outer ring
    const ring = rimPoints(7.5, 4.2);
    const lampsR = ring.filter((_, i) => i % 3 === 0),
      benchesR = ring.filter((_, i) => i % 3 === 1),
      plantersR = ring.filter((_, i) => i % 3 === 2);
    instanced(
      mergeAny([
        new THREE.CylinderGeometry(0.08, 0.13, 4.2, 10).translate(0, 2.1, 0),
        new THREE.CylinderGeometry(0.03, 0.03, 1.6, 6).rotateZ(PI / 2).translate(0, 4.1, 0),
      ]),
      M(0x2c3a33, { flatShading: false }),
      lampsR,
      (o, p) => {
        o.position.set(p.x - p.nx * 2.5, 0.3, p.z - p.nz * 2.5);
        o.rotation.y = p.ry;
      },
    );
    const heads = [];
    lampsR.forEach((p) => {
      for (const s of [-0.75, 0.75])
        heads.push({
          x: p.x - p.nx * 2.5 + Math.cos(p.ry) * s,
          z: p.z - p.nz * 2.5 - Math.sin(p.ry) * s,
        });
    });
    instanced(new THREE.SphereGeometry(0.24, 12, 10), lampHeadMat, heads, (o, h) =>
      o.position.set(h.x, 4.0, h.z),
    );
    instanced(benchGeo(), M(0xffffff, { vertexColors: true }), benchesR, (o, p) => {
      o.position.set(p.x - p.nx * 1.2, 0.3, p.z - p.nz * 1.2);
      o.rotation.y = p.ry + PI;
    });
    benchesR.forEach((p) => {
      if (rnd() < 0.45)
        sitSpots.push({
          x: p.x - p.nx * 1.2,
          y: 0.3 + 0.49 * BENCH - 0.04,
          z: p.z - p.nz * 1.2,
          ry: p.ry + PI,
        });
    });
    // raised planters with grass, bushes and a palm: breaks up the flat paving
    for (const p of plantersR) {
      const x = p.x - p.nx * 4.5,
        z = p.z - p.nz * 4.5;
      add(
        new THREE.CylinderGeometry(1.5, 1.6, 0.55, 24),
        M(0xe2d3b5, { flatShading: false }),
        x,
        0.57,
        z,
      );
      add(new THREE.CylinderGeometry(1.35, 1.35, 0.05, 24), grassMat, x, 0.86, z);
      for (let k = 0; k < 4; k++) {
        const a = (k * PI) / 2 + rr(0, 0.6);
        add(
          new THREE.IcosahedronGeometry(rr(0.35, 0.5), 1),
          M(pick([0x5e9a3c, 0x72ad48, 0x4f8a35]), { flatShading: false }),
          x + Math.cos(a) * 0.85,
          1.05,
          z + Math.sin(a) * 0.85,
        );
      }
      if (rnd() < 0.6) makePalm({ x, z });
      colliders.push({ type: 'ell', cx: x, cz: z, a: 1.6, b: 1.6 });
    }
    // a cycle lane painted along the ring
    const lane = rimPoints(10.5, 1.2);
    instanced(
      new THREE.BoxGeometry(1.25, 0.02, 1.3),
      M(0x9c6a58, { flatShading: false, roughness: 0.8 }),
      lane,
      (o, p) => {
        o.position.set(p.x, 0.375, p.z);
        o.rotation.y = p.ry + PI / 2;
      },
    ).castShadow = false;
    // people strolling the promenade: one closed loop, out along the sea side and back along the bench side
    const prom = { cx: 0, cz: 0 },
      path = [...rimPoints(3.6, 2.5), ...rimPoints(6, 2.5).reverse()].map((p) => [p.x, p.z]),
      lens = [];
    let total = 0;
    for (let k = 0; k < path.length; k++) {
      const [ax, az] = path[k],
        [bx, bz] = path[(k + 1) % path.length];
      lens.push(Math.hypot(bx - ax, bz - az));
      total += lens[k];
    }
    // some stroll in pairs: same pace, side by side
    for (let i = 0; i < 62; i++) {
      const s0 = rr(0, total),
        v = rr(0.42, 0.66),
        pair = rnd() < 0.35,
        group = [];
      for (let k = 0; k < (pair ? 2 : 1); k++) {
        const { g, legs } = makePerson(false);
        scene.add(g);
        group.push({
          g,
          legs,
          path,
          lens,
          total,
          b: prom,
          s: s0,
          v,
          ph: rr(0, 6),
          lat: 0,
          vv: 0,
          dist: 0,
          off: k * 0.34,
          dog: !k && rnd() < 0.15 ? makeDog() : null,
        });
      }
      if (pair) {
        group[0].mate = group[1];
        group[1].mate = group[0];
      }
      walkers.push(...group);
    }
    // beach bar (chiringuito) and an outdoor gym corner
    {
      const g = new THREE.Group();
      g.position.set(-55, 0.3, 20);
      g.rotation.y = PI / 2;
      scene.add(g);
      const wood = M(0xb88a5a, { flatShading: false }),
        white = M(0xf4efe4, { flatShading: false });
      add(new RoundedBoxGeometry(4.5, 2.2, 2.5, 2, 0.08), wood, 0, 1.1, 0, g);
      add(new THREE.BoxGeometry(5.5, 0.2, 3.6), white, 0, 2.4, 0.5, g);
      for (const x of [-2.5, 2.5])
        add(new THREE.CylinderGeometry(0.07, 0.07, 2.3, 8), wood, x, 1.2, 2.1, g);
      for (let k = 0; k < 4; k++) {
        const tx = -2 + k * 1.35;
        add(new THREE.CylinderGeometry(0.4, 0.4, 0.05, 16), white, tx, 0.75, 3.6, g);
        add(new THREE.CylinderGeometry(0.04, 0.04, 0.75, 6), white, tx, 0.38, 3.6, g);
        add(
          new THREE.ConeGeometry(1, 0.4, 12, 1, true),
          M(pick([0xd9412c, 0x2f6fd0, 0xf2c014]), { side: THREE.DoubleSide, flatShading: false }),
          tx,
          2.3,
          3.6,
          g,
        );
      }
      colliders.push({ type: 'rect', x0: -56.5, x1: -53.5, z0: 17.5, z1: 22.5 });
    }
    {
      const steel = P(0x7d8a96, { roughness: 0.4, metalness: 0.6 });
      for (let k = 0; k < 4; k++) {
        const x = 56,
          z = -10 + k * 3;
        add(new THREE.BoxGeometry(0.1, 2.4, 0.1), steel, x - 0.9, 1.5, z);
        add(new THREE.BoxGeometry(0.1, 2.4, 0.1), steel, x + 0.9, 1.5, z);
        add(new THREE.CylinderGeometry(0.04, 0.04, 1.9, 6).rotateZ(PI / 2), steel, x, 2.6, z);
      }
    }
  }
  // distant haze bank over the sea so the world fades instead of ending
  {
    const hazeTex = canvasTex(4, 128, (c) => {
      const g = c.createLinearGradient(0, 0, 0, 128);
      g.addColorStop(0, 'rgba(214,232,242,0)');
      g.addColorStop(0.6, 'rgba(214,232,242,.55)');
      g.addColorStop(1, 'rgba(214,232,242,.85)');
      c.fillStyle = g;
      c.fillRect(0, 0, 4, 128);
    });
    hazeTex.wrapS = hazeTex.wrapT = THREE.ClampToEdgeWrapping;
    const hz = new THREE.Mesh(
      new THREE.CylinderGeometry(150, 150, 60, 64, 1, true),
      new THREE.MeshBasicMaterial({
        map: hazeTex,
        transparent: true,
        depthWrite: false,
        side: THREE.BackSide,
        fog: false,
      }),
    );
    hz.position.y = 22;
    hz.renderOrder = -1;
    scene.add(hz);
    window.hazeRing = hz;
  }
  // ---------- drawn-world details: pigeons, a flower stall, flocks of birds ----------

  {
    // feral pigeon: plump blue-grey body, darker wings with two black bars, iridescent green-purple neck, small grey
    // head with a dark bill and white cere, black-tipped tail, coral feet. Origin at the feet, beak toward +z.
    const T = (g, c) => tint(g.index ? g.toNonIndexed() : g, c);
    const pg = mergeAny([
      T(new THREE.SphereGeometry(0.085, 14, 10).scale(1, 0.9, 1.45).translate(0, 0.1, 0), 0x8f96a3), // body
      T(new THREE.SphereGeometry(0.07, 12, 8).scale(1, 0.95, 1).translate(0, 0.12, 0.07), 0x7c8392), // breast
      T(new THREE.SphereGeometry(0.05, 12, 8).scale(1, 1.2, 1).translate(0, 0.175, 0.1), 0x5f8a7a), // neck sheen (green)
      T(
        new THREE.SphereGeometry(0.046, 12, 8).scale(1.02, 1.1, 1).translate(0, 0.165, 0.095),
        0x8a6a9a,
      ), // neck sheen (purple)
      T(new THREE.SphereGeometry(0.04, 12, 8).translate(0, 0.225, 0.13), 0x7d8494), // head
      T(
        new THREE.ConeGeometry(0.009, 0.035, 5).rotateX(PI / 2).translate(0, 0.222, 0.178),
        0x2a2a2a,
      ), // bill
      T(new THREE.SphereGeometry(0.009, 6, 4).translate(0, 0.232, 0.162), 0xf0eee8), // cere
      ...[-1, 1].flatMap((sx) => [
        T(
          new THREE.SphereGeometry(0.07, 10, 6)
            .scale(0.32, 0.55, 1.25)
            .translate(sx * 0.07, 0.11, -0.03),
          0x7a8190,
        ), // folded wing
        T(new THREE.BoxGeometry(0.006, 0.012, 0.05).translate(sx * 0.093, 0.11, -0.02), 0x2a2c30), // wing bars
        T(new THREE.BoxGeometry(0.006, 0.012, 0.05).translate(sx * 0.091, 0.095, -0.05), 0x2a2c30),
        T(new THREE.SphereGeometry(0.011, 6, 4).translate(sx * 0.028, 0.23, 0.155), 0xd8622a), // orange eye
        T(
          new THREE.CylinderGeometry(0.006, 0.006, 0.05, 4).translate(sx * 0.03, 0.025, 0.01),
          0xd9776a,
        ),
      ]), // legs
      T(
        new THREE.BoxGeometry(0.07, 0.015, 0.12).rotateX(-0.25).translate(0, 0.11, -0.15),
        0x6f7684,
      ), // tail
      T(
        new THREE.BoxGeometry(0.07, 0.016, 0.03).rotateX(-0.25).translate(0, 0.095, -0.205),
        0x2a2c30,
      ),
    ]); // tail tip
    for (const [cx, cz, n] of [
      [0, 15, 14],
      [-30, 6, 10],
      [30, -8, 12],
      [-50, -48, 9],
    ])
      for (let k = 0; k < n; k++)
        pigeons.push({ x: cx + rr(-3, 3), z: cz + rr(-2, 2), r: rr(0, PI * 2), ph: rr(0, 6) });
    window.pigeonMesh = instanced(
      pg,
      M(0xffffff, { vertexColors: true, flatShading: false, roughness: 0.7 }),
      pigeons,
      (o, p) => {
        o.position.set(p.x, 0.36, p.z);
        o.rotation.y = p.r;
      },
    );
    window.pigeonMesh.castShadow = false;
  }
  {
    // flower stall like on La Rambla, next to the Arc de Triomf stop
    const g = new THREE.Group();
    g.position.set(-56, 0.36, -44);
    g.rotation.y = 0.4;
    scene.add(g);
    add(
      new RoundedBoxGeometry(3, 1, 1.6, 2, 0.06),
      M(0x2f5b3a, { flatShading: false }),
      0,
      0.5,
      0,
      g,
    );
    add(
      new THREE.CylinderGeometry(1.9, 1.9, 0.12, 24, 1, false, 0, PI)
        .rotateZ(PI / 2)
        .scale(1, 1, 0.6),
      M(0x2f5b3a, { flatShading: false }),
      0,
      2.6,
      0,
      g,
    );
    for (const sx of [-1.4, 1.4])
      add(new THREE.CylinderGeometry(0.05, 0.05, 2.5, 8), M(0x223b2a), sx, 1.3, 0.7, g);
    for (let k = 0; k < 36; k++) {
      const x = -1.3 + (k % 9) * 0.32,
        z = -0.5 + Math.floor(k / 9) * 0.32,
        y = 1.05 + Math.floor(k / 9) * 0.12;
      add(
        new THREE.CylinderGeometry(0.11, 0.09, 0.16, 10),
        M(0x6b4a33, { flatShading: false }),
        x,
        y,
        z,
        g,
      );
      add(
        new THREE.IcosahedronGeometry(0.13, 1),
        M(pick([0xe8445a, 0xf5c542, 0xf28c28, 0xffffff, 0xc04fd6, 0xff7aa8]), {
          flatShading: false,
        }),
        x,
        y + 0.15,
        z,
        g,
      );
    }
    colliders.push({ type: 'ell', cx: -56, cz: -44, a: 1.9, b: 1.9 });
  }
}
